import { api } from "../../api/apiSlice";

// Guarding module, Phase 1 (TexasAlarms.Api's new src/Guarding module +
// Sales' pre-existing upcoming-deployments endpoint). Two independent
// pieces:
//
// 1) Pending Deployments - a thin GET wrapper over Sales' own
//    /sales/orders/upcoming-deployments (fixed server-side to stop
//    excluding orders with no RequestedDeploymentDate set). Deliberately
//    tagged "SalesOrder" (not a new tag) so the existing
//    useSetGuardDeploymentMutation from features/sales/salesApi.js - reused
//    as-is for the "Mark Deployed" action, not duplicated here - already
//    invalidates this list on save, with zero extra wiring.
//
// 2) Supervisor Areas - brand new CRUD (App_SupervisorAreaAssignment) plus
//    two picker lookups (employees/areas) served under this same module's
//    one permission, Deployment.ManageSupervisorAreas, so a holder of it
//    doesn't also need Employees.View/Area.View just to use this page.
export const guardingApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getUpcomingDeployments: builder.query({
      query: (params) => ({ url: "/sales/orders/upcoming-deployments", params }),
      providesTags: (result) =>
        result
          ? [...result.map((o) => ({ type: "SalesOrder", id: o.recordGuid })), { type: "SalesOrder", id: "LIST" }]
          : [{ type: "SalesOrder", id: "LIST" }],
    }),
    listSupervisorAreas: builder.query({
      query: (params) => ({ url: "/guarding/supervisor-areas", params }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((a) => ({ type: "GuardingSupervisorAreas", id: a.assignmentId })),
              { type: "GuardingSupervisorAreas", id: "LIST" },
            ]
          : [{ type: "GuardingSupervisorAreas", id: "LIST" }],
    }),
    replaceSupervisorAreas: builder.mutation({
      query: ({ employeeRecordGuid, ...body }) => ({
        url: `/guarding/supervisor-areas/${employeeRecordGuid}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: [{ type: "GuardingSupervisorAreas", id: "LIST" }],
    }),
    listGuardingEmployees: builder.query({
      query: (params) => ({ url: "/guarding/supervisor-areas/employees", params }),
    }),
    listGuardingAreas: builder.query({
      query: (params) => ({ url: "/guarding/supervisor-areas/areas", params }),
    }),
    // AI Guard Roster - guard-requirements is the narrow, no-money GuardDeploymentLineRecord
    // list (lives on Sales' own controller, gated Deployment.View like everything else here).
    // roster/candidates and confirm-selection are the new Guarding.Roster module.
    getGuardRequirementsForOrder: builder.query({
      query: (recordGuid) => ({ url: `/sales/orders/${recordGuid}/guard-requirements` }),
      providesTags: (result, error, recordGuid) => [{ type: "SalesOrder", id: recordGuid }],
    }),
    getGuardRosterCandidates: builder.query({
      query: (params) => ({ url: "/guarding/roster/candidates", params }),
    }),
    confirmGuardRosterSelection: builder.mutation({
      query: (body) => ({ url: "/guarding/roster/confirm-selection", method: "POST", data: body }),
      invalidatesTags: (result, error, body) => [{ type: "SalesOrder", id: body.orderRecordGuid }],
    }),
    // Guard Lookup (2026-09-05) - React port of the Flutter Attendance
    // module's own screen, surfaced here under the Guarding menu since
    // that's where a supervisor would look for "who guarded this
    // customer". Actually Attendance's endpoint (Batch126's
    // sp_attendance_guard_lookup), reused as-is rather than duplicated -
    // same cross-module pattern as getUpcomingDeployments above.
    getGuardLookup: builder.query({
      query: (params) => ({ url: "/attendance/dashboard/guard-lookup", params }),
    }),
    // Duty List (2026-09-14) - one branch-month of slots and who was on each
    // day (Batch171). Every view and filter on the page is built from this one
    // response client-side; only branch/month go to the server.
    getDutyList: builder.query({
      query: (params) => ({ url: "/attendance/duty-list", params }),
    }),

    // Attendance Dashboard (2026-09-09) - React port of the Flutter screen of the
    // same name, surfaced under Guarding for the same reason Guard Lookup is: it
    // answers "are my guards on post", which is guarding supervision, not HR
    // attendance. Same four Attendance endpoints the Flutter client calls, all
    // gated Attendance.Mark|Attendance.View.
    //
    // The server scopes them from the caller's own branch claims and, for a
    // plain supervisor (Attendance.Mark without Attendance.View), further down to
    // their assigned areas. Since 2026-10-02 each also takes an optional
    // `branchId` (validated against the caller's branches) and the screens pass
    // the header branch, so they follow it like every other page ("many pages are
    // still not triggering the global branch selection"). `isScopedToMyAreas` on
    // the summary is still the server's own answer about the area restriction.
    //
    // `shift` omitted is not a missing filter - it is "whatever shift is running
    // right now", resolved server-side from the configured changeover times so
    // every client agrees. Passing undefined leaves it off the query string.
    getAttendanceSummary: builder.query({
      query: (params) => ({ url: "/attendance/dashboard/summary", params }),
    }),
    // Fetched once per shift, marked AND not marked, then split client-side -
    // expanding either section is instant instead of a fresh round trip, exactly
    // as the Flutter screen does it.
    listAttendanceAssignments: builder.query({
      query: (params) => ({ url: "/attendance/dashboard/assignments", params }),
    }),
    listAttendanceMismatches: builder.query({
      query: (params) => ({ url: "/attendance/dashboard/mismatches", params }),
    }),
    // Supervisor -> area pairs for the dashboard's supervisor filter (empty for a
    // restricted supervisor). Its own read, not listSupervisorAreas, which needs
    // Deployment.ManageSupervisorAreas.
    listAttendanceSupervisorAreas: builder.query({
      query: (params) => ({ url: "/attendance/dashboard/supervisor-areas", params }),
    }),
    // groupBy AREA | SUPERVISOR, ordered worst-first server-side - finding who
    // needs chasing is the whole point, so the order is not ours to re-sort.
    listAttendancePerformance: builder.query({
      query: (params) => ({ url: "/attendance/dashboard/performance", params }),
    }),

    // Office Attendance Posts (2026-09-15, Batch173) - the "standing post" is the
    // duty slot an office employee's kiosk punch is credited to. It is a CARRIER,
    // not a claim about where they physically were: payroll reads only presence
    // per employee per day (fn_wrkingdays emits emp_Code + date, no assignment),
    // so this page is about keeping the kiosk able to mark people, not about
    // recording postings. Policy is Attendance.ManagePosts|Attendance.View.
    listStandingPosts: builder.query({
      query: (params) => ({ url: "/attendance/standing-posts", params }),
      providesTags: [{ type: "AttendanceStandingPosts", id: "LIST" }],
    }),
    // Office staff already working at our own premises with no slot yet - the
    // backlog. New joiners with no history never appear here; they are added
    // from the employee picker instead.
    listUnmappedStandingPosts: builder.query({
      query: (params) => ({ url: "/attendance/standing-posts/unmapped", params }),
      providesTags: [{ type: "AttendanceStandingPosts", id: "UNMAPPED" }],
    }),
    // Taken slots come back too, with their holder, so the picker can grey them
    // out rather than letting a save fail on the server's 1:1 rule.
    listStandingPostSlots: builder.query({
      query: (params) => ({ url: "/attendance/standing-posts/slots", params }),
      providesTags: [{ type: "AttendanceStandingPosts", id: "SLOTS" }],
    }),
    // Creating a post is served by the Attendance module itself, under the same
    // permission as the rest of this page, so a ManagePosts holder never needs
    // Deployment.Edit on the customer master to finish their own job. It wraps
    // the canonical single-assignment upsert, not the customer screen's
    // replace-all save.
    createStandingPostSlot: builder.mutation({
      query: (body) => ({ url: "/attendance/standing-posts/slots", method: "POST", data: body }),
      invalidatesTags: [{ type: "AttendanceStandingPosts", id: "SLOTS" }],
    }),
    assignStandingPost: builder.mutation({
      query: (body) => ({ url: "/attendance/standing-posts", method: "POST", data: body }),
      invalidatesTags: [
        { type: "AttendanceStandingPosts", id: "LIST" },
        { type: "AttendanceStandingPosts", id: "UNMAPPED" },
        { type: "AttendanceStandingPosts", id: "SLOTS" },
      ],
    }),
    endStandingPost: builder.mutation({
      query: (standingPostId) => ({ url: `/attendance/standing-posts/${standingPostId}`, method: "DELETE" }),
      invalidatesTags: [
        { type: "AttendanceStandingPosts", id: "LIST" },
        { type: "AttendanceStandingPosts", id: "UNMAPPED" },
        { type: "AttendanceStandingPosts", id: "SLOTS" },
      ],
    }),
    // "Fill from history" - applies only the unambiguous mappings and returns
    // what still needs a human decision. Safe to re-run; it never touches an
    // employee who already has a post.
    seedStandingPosts: builder.mutation({
      query: (params) => ({ url: "/attendance/standing-posts/seed", method: "POST", params }),
      invalidatesTags: [
        { type: "AttendanceStandingPosts", id: "LIST" },
        { type: "AttendanceStandingPosts", id: "UNMAPPED" },
        { type: "AttendanceStandingPosts", id: "SLOTS" },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetUpcomingDeploymentsQuery,
  useListSupervisorAreasQuery,
  useReplaceSupervisorAreasMutation,
  useListGuardingEmployeesQuery,
  useListGuardingAreasQuery,
  useGetGuardRequirementsForOrderQuery,
  useGetGuardRosterCandidatesQuery,
  useConfirmGuardRosterSelectionMutation,
  useLazyGetGuardLookupQuery,
  useGetDutyListQuery,
  useGetAttendanceSummaryQuery,
  useListAttendanceAssignmentsQuery,
  useLazyListAttendanceAssignmentsQuery,
  useListAttendanceMismatchesQuery,
  useListAttendancePerformanceQuery,
  useListAttendanceSupervisorAreasQuery,
  useListStandingPostsQuery,
  useListUnmappedStandingPostsQuery,
  useListStandingPostSlotsQuery,
  useCreateStandingPostSlotMutation,
  useAssignStandingPostMutation,
  useEndStandingPostMutation,
  useSeedStandingPostsMutation,
} = guardingApi;
